import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Suspense, lazy } from "react";
import Login from "./pages/Login";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ClientRouteProtection } from "./components/ClientRouteProtection";

// Create a simple LoadingScreen component directly
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-cs-purple-dark">
    <div className="text-center">
      <div className="animate-spin h-8 w-8 border-4 border-cs-purple border-t-transparent rounded-full mx-auto mb-4"></div>
      <p className="text-cs-purple dark:text-white">Loading...</p>
      <div className="mt-6 space-y-2">
        <p className="text-sm text-cs-purple dark:text-white/80">Taking too long? Try these links:</p>
        <div className="flex justify-center space-x-4">
          <a href="/login" className="text-cs-purple dark:text-white underline">Login</a>
        </div>
      </div>
    </div>
  </div>
);

// Error fallback component for routes
const RouteFallback = ({ error }: { error?: Error }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-cs-purple-dark">
    <div className="max-w-md w-full p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-center">
      <h2 className="text-2xl font-bold mb-4 text-red-500">Navigation Error</h2>
      <p className="mb-4">{error?.message || "There was an error loading this page"}</p>
      <div className="space-y-3">
        <a
          href="/login"
          className="block w-full px-4 py-2 bg-cs-purple text-white rounded hover:bg-cs-purple-dark text-center"
        >
          Go to Login
        </a>
        <div className="flex justify-between">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  </div>
);

// App initialization error handling
const AppInitializationError = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-cs-purple to-cs-purple-dark p-4">
    <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg text-center">
      <h2 className="text-2xl font-bold mb-4 text-cs-purple">City & Sanctuary</h2>
      <p className="mb-6">We encountered an error while loading the application.</p>
      <div className="space-y-4">
        <a
          href="/login"
          className="block w-full px-4 py-2 bg-cs-purple text-white rounded hover:bg-cs-purple-dark text-center"
        >
          Go to Login
        </a>
        <div className="flex justify-between">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Wrap lazy imports with error handling
const wrapLazy = (importer: () => Promise<any>) => {
  return lazy(() => {
    return importer().catch(error => {
      console.error("Error importing component:", error);
      return { 
        default: () => (
          <div className="p-8 bg-red-50 text-red-800 rounded-lg">
            <h3 className="font-bold text-lg mb-2">Loading Error</h3>
            <p>{error?.message || "Failed to load component"}</p>
            <button 
              onClick={() => window.location.href = "/login"}
              className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded"
            >
              Go to Login
            </button>
          </div>
        ) 
      };
    });
  });
};

// Lazy load non-critical pages
const Dashboard = wrapLazy(() => import("./pages/Dashboard"));
const ProjectDetail = wrapLazy(() => import("./pages/ProjectDetail"));
const EditProject = wrapLazy(() => import("./pages/EditProject"));
const NewProject = wrapLazy(() => import("./pages/NewProject"));
const TeamMembers = wrapLazy(() => import("./pages/TeamMembers"));
const Suppliers = wrapLazy(() => import("./pages/Suppliers"));
const ProjectLeads = wrapLazy(() => import("./pages/ProjectLeads"));
const Account = wrapLazy(() => import("./pages/Account"));
const NotFound = wrapLazy(() => import("./pages/NotFound"));
const FilePreview = wrapLazy(() => import("./pages/FilePreview"));
const Candidates = wrapLazy(() => import("./pages/Candidates"));

// Discovery Form pages - public access
const DiscoveryForm = wrapLazy(() => import("./pages/DiscoveryForm"));
const DiscoveryFormSuccess = wrapLazy(() => import("./pages/DiscoveryFormSuccess"));

// Create a single query client instance with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

// Use a simple component for wrapping providers with error boundaries
const SafeProviders = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundary fallback={<AppInitializationError />}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary fallback={<AppInitializationError />}>
          <AuthProvider>
            <ErrorBoundary fallback={<AppInitializationError />}>
              <TooltipProvider>
                {children}
              </TooltipProvider>
            </ErrorBoundary>
          </AuthProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

// Simplified PublicRoute component that doesn't interfere with routing
const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

const App = () => {
  return (
    <SafeProviders>
      <ErrorBoundary fallback={<RouteFallback />}>
        <Routes>
          {/* Public routes - simplified approach */}
          <Route path="/login" element={<Login />} />
          
          {/* Discovery Form - Public Routes */}
          <Route path="/discovery-form" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <DiscoveryForm />
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/discovery-form/success" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <DiscoveryFormSuccess />
              </ErrorBoundary>
            </Suspense>
          } />
          
          {/* Root route redirects to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* All protected routes grouped together with a simpler pattern */}
          <Route path="/dashboard" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/project/:id" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <ProjectDetail />
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/project/:id/edit" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <EditProject />
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/new" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <NewProject />
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/team" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <ClientRouteProtection>
                    <TeamMembers />
                  </ClientRouteProtection>
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/suppliers" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <ClientRouteProtection>
                    <Suppliers />
                  </ClientRouteProtection>
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/project-leads" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <ClientRouteProtection>
                    <ProjectLeads />
                  </ClientRouteProtection>
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/candidates" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <ClientRouteProtection>
                    <Candidates />
                  </ClientRouteProtection>
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          <Route path="/account" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          {/* New file preview route */}
          <Route path="/files/preview/:fileId" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <FilePreview />
                </ProtectedRoute>
              </ErrorBoundary>
            </Suspense>
          } />
          
          {/* Not found route - show actual 404 page for better UX */}
          <Route path="*" element={
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary fallback={<RouteFallback />}>
                <NotFound />
              </ErrorBoundary>
            </Suspense>
          } />
        </Routes>
      </ErrorBoundary>
      <Toaster />
      <Sonner />
    </SafeProviders>
  );
};

export default App;
